import React, { useMemo, useState } from "react";
import { useMachines } from "../../context/MachineContext";
import { useDepartments } from "../../context/DepartmentContext";
import Layout from "../../components/layout/Layout";
import MachineModal from "../../components/machines/MachineModal";
import { useColumnSearchProps } from "../../components/machines/GetColumnSearchProps";
import {
  PlusOutlined,
  SettingOutlined,
  EditOutlined,
  DeleteOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { Table, Popconfirm } from "antd";

const MachinesPage = () => {
  const { machines, loading, addMachine, deleteMachine, updateMachine } =
    useMachines();
  const { departments } = useDepartments();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // Güncelleme modu için
  const [selectedMachineId, setSelectedMachineId] = useState(null); // Seçilen makinenin ID'si
  const [errors, setErrors] = useState({});
  const getColumnSearchProps = useColumnSearchProps();
  const [newMachine, setNewMachine] = useState({
    name: "",
    code: "",
    status: "active",
    location: "",
    type: "production",
    efficiency: 100,
    machineYear: "",
    department: "",
  });

  const handleEditMachine = (machine) => {
    setNewMachine(machine); // Mevcut makine bilgilerini yükle
    setIsEditMode(true); // Güncelleme modunu aktif et
    setSelectedMachineId(machine._id); // Seçilen makinenin ID'sini sakla
    setIsModalOpen(true); // Modalı aç
  };

  const handleUpdateMachine = () => {
    if (!validateMachine()) return; // Validasyonu kontrol et

    updateMachine(selectedMachineId, newMachine);
    handleCloseModal();
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false); // Güncelleme modunu kapat
    setSelectedMachineId(null); // Seçilen makine ID'sini temizle
    setNewMachine({
      name: "",
      code: "",
      status: "active",
      location: "",
      type: "",
      efficiency: 100,
      machineYear: new Date().getFullYear(),
      department: "",
    });
    setErrors({});
  };

  const validateMachine = () => {
    const requiredFields = [
      "name",
      "code",
      "status",
      "location",
      "type",
      "efficiency",
      "machineYear",
      "department",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!newMachine[field]) {
        newErrors[field] = `${field.replace(/([A-Z])/g, " $1")} is required.`; // Dinamik hata mesajı
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Hata yoksa true döner
  };
  const handleSave = () => {
    if (!validateMachine()) return; // Eğer hata varsa işlemi durdur

    addMachine(newMachine);
    handleCloseModal();
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return { color: "green", text: "Aktif" };
      case "inactive":
        return { color: "red", text: "Pasif" };
      case "maintenance":
        return { color: "gold", text: "Bakımda" };
      default:
        return { color: "gray", text: "Bilinmiyor" };
    }
  };

  const getMachineType = (type) => {
    switch (type) {
      case "production":
        return { color: "blue", text: "Ürün" };
      case "quality":
        return { color: "orange", text: "Kalite" };
      case "assembly":
        return { color: "gold", text: "Üretim" };
      default:
        return { color: "gray", text: "Bilinmiyor" };
    }
  };

  const departmentMap = useMemo(() => {
    return departments.reduce((map, department) => {
      map[department._id] = department.name;
      return map;
    }, {});
  }, [departments]);

  const columns = [
    {
      dataIndex: "icon",
      key: "icon",
      width: "1%",
      render: (status) => <SettingOutlined />,
    },
    {
      title: "M.Kodu",
      dataIndex: "code",
      key: "code",
      width: "10%",
      ...getColumnSearchProps("code"),
      sorter: (a, b) => a.code.length - b.code.length,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "M. Adı",
      dataIndex: "name",
      key: "name",
      width: "20%",
      ...getColumnSearchProps("name"),
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "M.Durumu",
      dataIndex: "status",
      key: "status",
      width: "10%",
      filters: [
        { text: "Aktif", value: "active" },
        { text: "Pasif", value: "inactive" },
        { text: "Bakımda", value: "maintenance" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const { color, text } = getStatusColor(status);
        return <span style={{ color }}>{text}</span>;
      },
    },
    {
      title: "Lokasyon",
      dataIndex: "location",
      key: "location",
      width: "15%",
      ...getColumnSearchProps("location"),
      sorter: (a, b) => a.location.localeCompare(b.location),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "M. Tipi",
      dataIndex: "type",
      key: "type",
      render: (type) => {
        const { color, text } = getMachineType(type);
        return <span style={{ color }}>{text}</span>;
      },
    },
    {
      title: "M. Yılı",
      dataIndex: "machineYear",
      key: "machineYear",
      sorter: (a, b) => a.machineYear - b.machineYear,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Departman",
      dataIndex: "department",
      key: "department",
      sorter: (a, b) =>
        (departmentMap[a.department] || "").localeCompare(
          departmentMap[b.department] || ""
        ),
      sortDirections: ["descend", "ascend"],
      render: (departmentId) => (
        <span>
          {departmentMap[departmentId] && (
            <ApartmentOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          )}
          {departmentMap[departmentId] || "Bilinmeyen Departman"}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (machine) => (
        <span>
          {/* Düzenleme Butonu */}
          <EditOutlined
            style={{ marginRight: 10 }}
            onClick={() => handleEditMachine(machine)}
          />

          {/* Silme Butonu (Popconfirm ile) */}
          <Popconfirm
            title="Bu makineyi silmek istediğinize emin misiniz?"
            onConfirm={() => deleteMachine(machine._id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <DeleteOutlined style={{ color: "red" }} />
          </Popconfirm>
        </span>
      ),
    },
  ];

  if (loading) {
    return <div>Loading...</div>;
  }
  return (
    <Layout>
      <div className="mt-16 ml-64">
        <MachineModal
          isModalOpen={isModalOpen}
          handleCloseModal={handleCloseModal}
          newMachine={newMachine}
          setNewMachine={setNewMachine}
          errors={errors}
          handleSave={isEditMode ? handleUpdateMachine : handleSave} // Güncelleme veya ekleme işlemi
          departments={departments}
          isEditMode={isEditMode} // Modalın güncelleme modunda olduğunu belirt
        />
        {/* Üst Alan */}
        <div className="flex justify-between items-center p-4 bg-white shadow-md rounded-lg mb-4">
          <h2 className="text-xl font-semibold">Makineler</h2>
          <div className="flex gap-4 items-center">
            <button
              className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center gap-2"
              onClick={handleOpenModal}
            >
              <PlusOutlined /> Makine Ekle
            </button>
          </div>
        </div>

        <Table columns={columns} dataSource={machines} />
      </div>
    </Layout>
  );
};

export default MachinesPage;
